import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { SHORTEST_TERM, search, type SearchHit } from '../api/search'
import { useSession } from '../useSession'

/**
 * The question that used to be a rail item (D-325).
 *
 * **Every other destination is a place; "Find a dancer" was a question.** It sat
 * in the header as a link to a screen whose only job was to hold a text box,
 * which is a place invented to house a question. This is the question.
 *
 * **Scoped by the server, per row.** A parent finds her own household, a teacher
 * the dancers she teaches, and a dual-role account the union (D-307). Nothing
 * here filters — the browser is told only what this account may see, because the
 * alternative was granting `ViewPeople` to make a search box work.
 *
 * **Not on a classroom tablet.** The header is shared, so without this the box
 * arrives on a PIN-unlocked device in a room full of dancers, and D-233 records
 * that the boundary fails *open*.
 */
export default function SearchBox() {
  const { session } = useSession()
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const usable = term.trim().length >= SHORTEST_TERM

  const { data, isFetching } = useQuery({
    queryKey: ['search', term.trim()],
    queryFn: () => search(term.trim()),
    enabled: usable && open,

    /*
     * Keeps the last answer on screen while the next one loads, so typing a
     * sixth letter does not blank the list and make it flicker.
     */
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    const onClick = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  if (!session || session.isKiosk) return null

  const hits = data?.hits ?? []

  function go(hit: SearchHit) {
    // A hit with nowhere to go still answers the question - she asked who
    // somebody is, and the row says so.
    if (!hit.href) return

    setOpen(false)
    setTerm('')
    navigate(hit.href)
  }

  return (
    /*
     * **Not positioned on a phone, so the results hang from the header row** - the
     * same fix as the bell's panel, for the same cause. The 288px panel was pinned to
     * the box's right edge, and on a 390px phone the header puts the box at the left of
     * its row, so the results began off the left of the screen. From `sm` up they stay
     * under the box.
     */
    <div ref={container} className="sm:relative">
      <label className="flex items-center gap-1.5 rounded-lg border border-line-2 bg-surface px-2.5 py-1.5">
        <Search size={15} className="shrink-0 text-ink-3" aria-hidden="true" />

        <input
          value={term}
          onChange={(e) => {
            setTerm(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search"
          aria-label="Search dancers, classes and staff"
          /*
           * py-1 for the height. A 13px input is 20px tall on its own, under
           * the 24px floor - and unlike a checkbox, a text box is dragged into
           * as well as tapped, so crediting the label around it would be the
           * wrong call (D-255).
           */
          className="w-28 bg-transparent py-1 text-[13px] text-ink outline-none placeholder:text-ink-3 sm:w-40"
        />
      </label>

      {open && usable && (
        <div
          data-panel="search-results"
          className="absolute top-full right-5 left-5 z-50 mt-1.5 rounded-xl border border-line bg-surface p-1.5 shadow-xl sm:right-0 sm:left-auto sm:w-72"
        >
          {hits.length === 0 && (
            <p className="px-2.5 py-4 text-center text-[13px] text-ink-3">
              {isFetching ? (
                'Looking…'
              ) : data?.narrowed ? (
                /*
                 * "Nothing of yours" rather than "nothing" (D-209). Only the
                 * server knows the answer was scoped, and a parent told
                 * flatly that there is no such dancer would conclude the
                 * studio has lost her child.
                 */
                <>Nothing of yours matches that.</>
              ) : (
                <>Nothing matches that.</>
              )}
            </p>
          )}

          {hits.map((hit) => (
            <button
              key={`${hit.kind}-${hit.id}`}
              type="button"
              onClick={() => go(hit)}
              className="block w-full rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-surface-2"
            >
              <span className="block text-[13px] font-semibold wrap-anywhere text-ink">
                {hit.title}
              </span>

              <span className="mt-0.5 block text-[11.5px] text-ink-3">
                {hit.kind}
                {hit.detail && <span className="wrap-anywhere"> &middot; {hit.detail}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
